'use client';
import { useBulkSendResultToast } from '@/hooks/chat/useBulkSendResultToast';
import { useChannelCountersLive } from '@/hooks/useChannelCountersLive';
import { useChannelActivityToast } from '@/hooks/useChannelActivityToast';
import { useServiceWorkerRegistration } from '@/hooks/useServiceWorkerRegistration';
import { useUnreadSignals } from '@/hooks/useUnreadSignals';
import { AuthProvider } from '@/providers/AuthContext';
import { SocketProvider } from '@/providers/SocketContext';
import { PresenceProvider } from '@/providers/PresenceContext';
import { NavigationProvider } from '@/providers/NavigationContext';
import { BloqueioDeRede } from '@/components/RedeBloqueada/BloqueioDeRede';
import { AppRail } from '@/components/layout/AppRail';
import { PresenceDrawer } from '@/components/Presence/PresenceDrawer';
import { NotificationsEffects } from '@/components/notifications/NotificationsEffects';
// Import só pelo efeito, e é ele que arma a captura do `beforeinstallprompt` —
// não remover. O Chromium dispara o evento UMA vez, logo depois que os critérios
// de instalação são satisfeitos, e não o reemite em navegação de client-side; um
// ouvinte que só nascesse com a seção Notificações de /conta chegaria tarde e o
// botão "Instalar" nunca apareceria. Aqui, e não no layout raiz, porque este é o
// boot CLIENTE da área privada — é onde o botão "Instalar" vive (`/conta`) e é o
// primeiro ponto que roda para quem já entrou. NÃO é porque os critérios
// dependeriam do service worker: desde o Chrome 108/112 a instalabilidade não
// exige service worker (ver `app/manifest.md`), e este arquivo afirmou o
// contrário até 2026-09-04.
// Travado por `PrivateProviders.installPrompt.test.ts`.
import '@/lib/platform/installPrompt';
import React from 'react';
import { PortaoDeSessao } from './PortaoDeSessao';

function ShellPrivado({ children }: { children: React.ReactNode }) {
  // Resumo de não-enviados de disparo de template — global: o atendente pode
  // navegar pra qualquer página entre o disparo e a chegada dos eventos.
  useBulkSendResultToast();
  // Contadores por canal ao vivo. App-wide de propósito: o badge do rail vale em
  // /admin, /conta e /notifications, onde o ChatBotProvider não existe.
  useChannelCountersLive();
  // Aviso de atividade nos canais do escopo. App-wide pelo mesmo motivo: o evento
  // chega esteja o atendente onde estiver.
  useChannelActivityToast();
  // Service worker de notificação. App-wide e uma vez só: `showNotification()` da
  // ServiceWorkerRegistration é o ÚNICO caminho que funciona em mobile (P1), e a
  // registration é lida por um singleton de módulo — não por contexto.
  useServiceWorkerRegistration();
  // Contador no título da janela e no ícone do app. App-wide porque o número
  // vale em qualquer rota; ESTADO, não notificação — a pref escolhe QUAL número
  // (mensagens ou notificações do inbox), nenhuma o desliga.
  useUnreadSignals();

  return (
    /*
      `viewportFit: 'cover'` (app/layout.tsx) manda o app desenhar sob os
      entalhes e sob a barra de gestos — e cobra o inset de volta. O consumo mora
      AQUI, e não em cada barra colada na borda: um ponto só cobre toda rota
      privada (chat incluso) e não deixa a regra pra ser esquecida em folha nova.

      Com `box-sizing: border-box` o `h-dvh` não cresce: o conteúdo é que
      encolhe, que é o comportamento certo. O `top` entra por causa do
      `appleWebApp.statusBarStyle: 'black-translucent'` do iOS, que joga o
      conteúdo sob a barra de status — no Android standalone esse inset é 0 e não
      custa nada. Fallback `0px` obrigatório: `env()` sem ele invalida a
      declaração inteira onde não há suporte.
    */
    <div
      className="flex h-dvh w-full overflow-hidden"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <AppRail />
      {/*
        Consome a `--ds-sidebar-push` que o DialogSidebar escreve no <html> ao
        ficar pinado (keepOpen + modo sidebar + aberto). Sem isto a variável é
        escrita e ignorada, e o painel de filtros SOBREPÕE o conteúdo em vez de
        comprimi-lo — com `modal={false}` o usuário pode clicar no que está
        embaixo, só que não enxerga.

        Sem transition, de propósito (mesma escolha da origem no omnicare): o
        shift é instantâneo e o sheet anima entrando no espaço já alocado.
        Animar os dois some com o alinhamento, e o handle de resize arrasta a
        largura em tempo real — com transition o conteúdo persegue o ponteiro.
      */}
      <div
        className="min-w-0 flex-1 overflow-hidden"
        style={{ paddingRight: 'var(--ds-sidebar-push, 0px)' }}
      >
        {children}
      </div>
      <PresenceDrawer />
      {/* Uma única instância app-wide: toast/modal/browser notif do inbox
          valem em qualquer rota privada, não só na tela de notificações. */}
      <NotificationsEffects />
    </div>
  );
}

/**
 * Todo o shell client das rotas privadas. Mora aqui, e não no `layout.tsx`, porque
 * um `layout.tsx` com `'use client'` é montado pelo Next dentro de um wrapper extra
 * no SSR que não existe na árvore reconstruída no cliente — a árvore do cliente sai
 * um nível mais rasa e todo `useId()` abaixo (os `aria-controls` de Popover/Tooltip
 * do Radix na `AppRail`) diverge do HTML do servidor. Ver `layout.test.ts`.
 * Tudo atrás do `PortaoDeSessao`: nada privado monta antes da renovação inicial (faixa 25 §8).
 */
export function PrivateProviders({ children }: { children: React.ReactNode }) {
  return (
    <PortaoDeSessao>
      <AuthProvider>
        <SocketProvider>
          <PresenceProvider>
            <NavigationProvider>
              <ShellPrivado>{children}</ShellPrivado>
            </NavigationProvider>
          </PresenceProvider>
        </SocketProvider>
        {/* Tela de bloqueio por rede (faixa 28 §4): POR CIMA do app, irmã da árvore — os filhos ficam montados
            (rascunho, aba, filtros). Dentro do AuthProvider: o "Sair" dela é o logout de sempre. */}
        <BloqueioDeRede />
      </AuthProvider>
    </PortaoDeSessao>
  );
}
